// ========================================
// pages/AssetDetails.js - View Single Asset Details
// ========================================
// LAST MODIFIED: April 18, 2026
// 
// NOTES:
// - Assets are IMMUTABLE (cannot be edited)
// - Only "Created" date shown (removed "Last Updated")
// - Footer included for navigation to public pages
// - Supports 8 asset types: bank_account, real_estate, vehicle, will,
//   secure_note, login_credential, license_code, custom_asset
// ========================================

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Footer from '../components/Footer';

const AssetDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // ========================================
  // STATE VARIABLES
  // ========================================
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDecrypted, setShowDecrypted] = useState(false);
  const [pin, setPin] = useState('');
  const [decryptedBalance, setDecryptedBalance] = useState(null);
  const [decrypting, setDecrypting] = useState(false);
  const [decryptError, setDecryptError] = useState('');
  const [downloadingDoc, setDownloadingDoc] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // ========================================
  // HELPER: GET CURRENCY SYMBOL
  // ========================================
  const getCurrencySymbol = (code) => {
    const symbols = { 
      GHS: '₵', USD: '$', GBP: '£', EUR: '€', 
      NGN: '₦', ZAR: 'R', CAD: 'CA$', AUD: 'A$' 
    };
    return symbols[code] || code;
  };

  // ========================================
  // HELPER: GET ASSET ICON
  // ========================================
  const getAssetIcon = () => {
    const icons = {
      bank_account: '🏦',
      real_estate: '🏠',
      vehicle: '🚗',
      will: '📜',
      secure_note: '🔒',
      login_credential: '🔐',
      license_code: '📜',
      custom_asset: '📋'
    };
    return icons[asset?.assetType] || '📦';
  };

  // ========================================
  // HELPER: FORMAT DATE
  // ========================================
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // ========================================
  // FETCH ASSET DATA
  // ========================================
  useEffect(() => {
    fetchAsset();
  }, [id]);

  const fetchAsset = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/assets/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAsset(response.data);
    } catch (err) {
      setError('Failed to load asset details');
      console.error('Error fetching asset:', err);
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // DECRYPT BALANCE (Bank Account)
  // ========================================
  const handleDecrypt = async () => {
    if (!pin) {
      setDecryptError('Please enter your PIN');
      return;
    }
    
    setDecrypting(true);
    setDecryptError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/assets/${id}/decrypt`, 
        { pin },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setDecryptedBalance(response.data.decryptedBalance);
      setShowDecrypted(true);
    } catch (err) {
      setDecryptError(err.response?.data?.error || 'Invalid PIN or decryption failed');
    } finally {
      setDecrypting(false);
    }
  };

  // ========================================
  // DOWNLOAD PDF CERTIFICATE
  // ========================================
  const downloadPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/assets/${id}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `asset-${asset.certificateNumber || asset.assetName || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download PDF. Please try again.');
    }
  };

  // ========================================
  // DOWNLOAD SUPPORTING DOCUMENT
  // ========================================
  const downloadDocument = async () => {
    if (!asset.document || !asset.document.fileUrl) {
      alert('No document available for this asset');
      return;
    }
    
    setDownloadingDoc(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/assets/${id}/document`, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: asset.document.fileType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', asset.document.fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download document.');
    } finally {
      setDownloadingDoc(false);
    }
  };

  // ========================================
  // DELETE ASSET
  // ========================================
  const handleDelete = async () => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/assets/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setShowDeleteModal(false);
      navigate('/dashboard');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete asset. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // ========================================
  // RENDER BANK ACCOUNT FIELDS
  // ========================================
  const renderBankAccountFields = () => (
    <>
      <div className="info-item">
        <label>Institution</label>
        <p>{asset.institutionName || asset.data?.institutionName || '—'}</p>
      </div>
      <div className="info-item">
        <label>Account Number</label>
        <p>{asset.accountNumber || asset.data?.accountNumber || '—'}</p>
      </div>
      <div className="info-item">
        <label>Branch</label>
        <p>{asset.branch || asset.data?.branch || '—'}</p>
      </div>
      <div className="info-item">
        <label>Balance</label>
        {showDecrypted ? (
          <p className="balance-value">{getCurrencySymbol(asset.currency || 'GHS')}{decryptedBalance}</p>
        ) : (
          <div className="balance-locked">
            <p>🔒 Encrypted</p>
            <div className="pin-box">
              <input
                type="password"
                placeholder="Enter PIN"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                className="pin-input"
              />
              <button onClick={handleDecrypt} disabled={decrypting} className="decrypt-btn">
                {decrypting ? 'Verifying...' : 'Unlock'}
              </button>
            </div>
            {decryptError && <p className="error-text">{decryptError}</p>}
          </div>
        )}
      </div>
    </>
  );

  // ========================================
  // RENDER REAL ESTATE FIELDS
  // ========================================
  const renderRealEstateFields = () => (
    <>
      <div className="info-item">
        <label>Property Type</label>
        <p>{asset.propertyType || asset.data?.propertyType || '—'}</p>
      </div>
      <div className="info-item">
        <label>Address</label>
        <p>{asset.address || asset.data?.address || '—'}</p>
      </div>
      <div className="info-item">
        <label>Title Deed Number</label>
        <p>{asset.titleDeedNumber || asset.data?.titleDeedNumber || '—'}</p>
      </div>
      <div className="info-item">
        <label>Estimated Value</label>
        <p>{getCurrencySymbol(asset.currency || 'GHS')}{asset.estimatedValue?.toLocaleString() || '—'}</p>
      </div>
    </>
  );

  // ========================================
  // RENDER VEHICLE FIELDS
  // ========================================
  const renderVehicleFields = () => (
    <>
      <div className="info-item">
        <label>Make & Model</label>
        <p>{asset.make} {asset.model}</p>
      </div>
      <div className="info-item">
        <label>Year</label>
        <p>{asset.year || '—'}</p>
      </div>
      <div className="info-item">
        <label>VIN</label>
        <p>{asset.vin || '—'}</p>
      </div>
      <div className="info-item">
        <label>Estimated Value</label>
        <p>{getCurrencySymbol(asset.currency || 'GHS')}{asset.estimatedValue?.toLocaleString() || '—'}</p>
      </div>
    </>
  );

  // ========================================
  // RENDER SECURE NOTE FIELDS
  // ========================================
  const renderSecureNoteFields = () => (
    <div className="asset-detail-section">
      <h3 className="section-title">🔒 Secure Note Content</h3>
      <div className="secure-note-content">
        <div className="secure-note-field">
          <label>Note Title:</label>
          <p>{asset.secureNoteTitle || 'Untitled'}</p>
        </div>
        <div className="secure-note-field">
          <label>Content:</label>
          <div className="secure-note-text">
            {asset.secureNoteContentDecrypted || 'No content available'}
          </div>
        </div>
      </div>
    </div>
  );

  // ========================================
  // RENDER LOGIN CREDENTIAL FIELDS
  // ========================================
  const renderLoginCredentialFields = () => (
    <div className="asset-detail-section">
      <h3 className="section-title">🔐 Login Credential</h3>
      <div className="login-credential-content">
        <div className="credential-field">
          <label>Platform:</label>
          <p>{asset.platformName || '—'}</p>
        </div>
        <div className="credential-field">
          <label>Username/Email:</label>
          <p>{asset.username || '—'}</p>
        </div>
        <div className="credential-field">
          <label>Password:</label>
          <p className="password-value">{asset.passwordDecrypted || '—'}</p>
        </div>
        {asset.loginUrl && (
          <div className="credential-field">
            <label>Login URL:</label>
            <a href={asset.loginUrl} target="_blank" rel="noopener noreferrer">{asset.loginUrl}</a>
          </div>
        )}
      </div>
    </div>
  );

  // ========================================
  // RENDER LICENSE/CODE FIELDS
  // ========================================
  const renderLicenseCodeFields = () => (
    <div className="asset-detail-section">
      <h3 className="section-title">📜 License/Code</h3>
      <div className="license-content">
        <div className="license-field">
          <label>Product:</label>
          <p>{asset.productName || '—'}</p>
        </div>
        <div className="license-field">
          <label>License Key:</label>
          <p className="license-value">{asset.licenseKeyDecrypted || '—'}</p>
        </div>
        {asset.expiryDate && (
          <div className="license-field">
            <label>Expiry Date:</label>
            <p>{new Date(asset.expiryDate).toLocaleDateString()}</p>
          </div>
        )}
        {asset.vendor && (
          <div className="license-field">
            <label>Vendor:</label>
            <p>{asset.vendor}</p>
          </div>
        )}
      </div>
    </div>
  );

  // ========================================
  // RENDER CUSTOM ASSET FIELDS
  // ========================================
  const renderCustomAssetFields = () => (
    <div className="asset-detail-section">
      <h3 className="section-title">📋 Custom Asset</h3>
      <div className="custom-asset-content">
        {asset.customFields && asset.customFields.length > 0 ? (
          asset.customFields.map((field, idx) => (
            <div key={idx} className="custom-field">
              <label>{field.fieldName}:</label>
              <p>{field.fieldValue}</p>
            </div>
          ))
        ) : (
          <p className="no-fields">No custom fields added.</p>
        )}
      </div>
    </div>
  );

  // ========================================
  // LOADING STATE
  // ========================================
  if (loading) {
    return (
      <div className="asset-details-container">
        <div className="loading-card">
          <div className="spinner"></div>
          <p>Loading asset details...</p>
        </div>
      </div>
    );
  }

  // ========================================
  // ERROR STATE
  // ========================================
  if (error || !asset) {
    return (
      <div className="asset-details-container">
        <div className="error-card">
          <h2>Asset Not Found</h2>
          <p>{error || 'The asset you are looking for does not exist.'}</p>
          <Link to="/dashboard" className="back-button">← Back to Dashboard</Link>
        </div>
      </div>
    );
  }

  // ========================================
  // MAIN RENDER
  // ========================================
  return (
    <div className="asset-details-container">
      
      {/* Header */}
      <div className="asset-header">
        <Link to="/dashboard" className="back-link">← Back to Dashboard</Link>
        <h1 className="asset-title">{asset.assetName || 'Asset Details'}</h1>
        <div className="header-buttons">
          <button onClick={downloadPDF} className="btn-pdf">📄 Download PDF</button>
          {asset.document?.fileUrl && (
            <button onClick={downloadDocument} disabled={downloadingDoc} className="btn-doc">
              {downloadingDoc ? 'Downloading...' : '📎 Download Document'}
            </button>
          )}
          <button onClick={() => setShowDeleteModal(true)} className="btn-delete">🗑️ Delete</button>
        </div>
      </div>

      {/* Main Card */}
      <div className="asset-card">
        
        {/* Type Badge */}
        <div className="type-badge">
          <span className="type-icon">{getAssetIcon()}</span>
          <span className="type-text">{asset.assetType?.replace('_', ' ').toUpperCase()}</span>
          {asset.certificateNumber && (
            <span className="cert-badge">Cert: {asset.certificateNumber}</span>
          )}
        </div>

        {/* Integrity Warning */}
        {asset.integrityWarning && (
          <div className="integrity-warning">
            <span className="warning-icon">⚠️</span>
            <span className="warning-text">{asset.integrityWarning}</span>
          </div>
        )}

        {/* Asset Info Grid */}
        <div className="info-grid">
          
          {/* Common Fields */}
          <div className="info-item">
            <label>Asset Name</label>
            <p>{asset.assetName || '—'}</p>
          </div>
          
          <div className="info-item">
            <label>Description</label>
            <p>{asset.description || asset.data?.description || '—'}</p>
          </div>

          {/* Type-Specific Fields */}
          {asset.assetType === 'bank_account' && renderBankAccountFields()}
          {asset.assetType === 'real_estate' && renderRealEstateFields()}
          {asset.assetType === 'vehicle' && renderVehicleFields()}
          {asset.assetType === 'secure_note' && renderSecureNoteFields()}
          {asset.assetType === 'login_credential' && renderLoginCredentialFields()}
          {asset.assetType === 'license_code' && renderLicenseCodeFields()}
          {asset.assetType === 'custom_asset' && renderCustomAssetFields()}

          {/* Location */}
          {(asset.location?.fullAddress || asset.location?.state || asset.location?.country || asset.location?.gpsCoordinates) && (
            <div className="info-item full-width">
              <label>Location</label>
              <p>
                {asset.location.fullAddress && <>{asset.location.fullAddress}<br /></>}
                {asset.location.state && <>{asset.location.state}<br /></>}
                {asset.location.country && <>{asset.location.country}<br /></>}
                {asset.location.gpsCoordinates && <>GPS: {asset.location.gpsCoordinates}</>}
              </p>
            </div>
          )}

          {/* Created Date */}
          <div className="info-item">
            <label>Created</label>
            <p>{formatDate(asset.createdAt)}</p>
          </div>

          {/* Private Notes */}
          {asset.notes && (
            <div className="info-item full-width">
              <label>Private Notes</label>
              <p className="notes-text">{asset.notes}</p>
            </div>
          )}
        </div>

        {/* Document Section */}
        {asset.document?.fileName && (
          <div className="document-section">
            <h3>📎 Supporting Document</h3>
            <div className="document-info">
              <p><strong>File Name:</strong> {asset.document.fileName}</p>
              <p><strong>File Type:</strong> {asset.document.fileType}</p>
              <p><strong>File Size:</strong> {(asset.document.fileSize / 1024).toFixed(2)} KB</p>
              <p><strong>Uploaded:</strong> {formatDate(asset.document.uploadedAt)}</p>
            </div>
          </div>
        )}

        {/* Integrity Verification Section */}
        {asset.hash && (
          <div className="proof-section">
            <h3>🔗 Integrity Verification</h3>
            <div className="proof-box">
              <p className="proof-label">Hash:</p>
              <code className="proof-hash">{asset.hash}</code>
              <p className="proof-label">Status:</p>
              <p className={asset.integrityWarning ? 'tampered-status' : 'verified-status'}>
                {asset.integrityWarning ? '⚠️ TAMPERED' : '✓ VERIFIED'}
              </p>
              {!asset.integrityWarning && (
                <p className="proof-date">Verified: {formatDate(asset.createdAt)}</p>
              )}
            </div>
          </div>
        )}

        {/* Beneficiary Section */}
        <div className="beneficiary-section">
          <h3>👥 Beneficiary</h3>
          <div className="beneficiary-box">
            <p><strong>Assignment Type:</strong> {asset.beneficiaryType?.replace('_', ' ') || 'Default'}</p>
            <p><strong>Status:</strong> {asset.status || 'Active'}</p>
          </div>
        </div>
        
      </div>

      {/* Footer */}
      <Footer />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-icon">⚠️</div>
            <h2 className="modal-title">Delete Asset?</h2>
            <p className="modal-text">
              Are you sure you want to delete <strong>"{asset.assetName}"</strong>?
            </p>
            <p className="modal-warning">
              This action <strong>cannot be undone</strong>. The asset certificate will become invalid.
              If you made a mistake, you will need to create a new asset from scratch.
            </p>
            <div className="modal-buttons">
              <button onClick={() => setShowDeleteModal(false)} className="modal-cancel" disabled={deleting}>
                Cancel
              </button>
              <button onClick={handleDelete} className="modal-delete" disabled={deleting}>
                {deleting ? 'Deleting...' : 'Yes, Delete Asset'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style dangerouslySetInnerHTML={{ __html: `
        .asset-details-container {
          min-height: 100vh;
          background: #F5F0E6;
          padding: 20px;
          font-family: 'Montserrat', sans-serif;
          display: flex;
          flex-direction: column;
        }
        
        .asset-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
          max-width: 1000px;
          margin: 0 auto 30px;
          flex-wrap: wrap;
          gap: 15px;
        }
        
        .back-link {
          color: #2E7D32;
          text-decoration: none;
          font-size: 14px;
        }
        
        .asset-title {
          font-size: 24px;
          color: #1A1A1A;
          margin: 0;
        }
        
        .header-buttons {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }
        
        .btn-pdf, .btn-doc, .btn-delete {
          padding: 10px 20px;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          font-weight: bold;
          font-size: 14px;
          transition: all 0.3s;
          min-height: 44px;
        }
        
        .btn-pdf { background: #FFD700; color: #1A1A1A; }
        .btn-doc { background: #2E7D32; color: white; }
        .btn-delete { background: #dc2626; color: white; }
        
        .asset-card {
          background: white;
          border-radius: 10px;
          padding: 30px;
          max-width: 1000px;
          margin: 0 auto;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        }
        
        .type-badge {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 16px;
          background: #F5F0E6;
          border-radius: 20px;
          margin-bottom: 30px;
          flex-wrap: wrap;
        }
        
        .type-icon { font-size: 20px; }
        .type-text { font-size: 14px; font-weight: bold; color: #2E7D32; }
        
        .cert-badge { 
          font-size: 12px; 
          background: #FFD700; 
          color: #1A1A1A; 
          padding: 4px 8px; 
          border-radius: 4px;
          white-space: nowrap;
        }
        
        .integrity-warning {
          background: #fee2e2;
          border: 1px solid #ef4444;
          border-radius: 8px;
          padding: 12px;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }
        
        .warning-icon { font-size: 20px; }
        .warning-text { font-size: 14px; color: #dc2626; flex: 1; }
        
        .info-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
          margin-bottom: 30px;
        }
        
        .info-item {
          border-bottom: 1px solid #eee;
          padding-bottom: 10px;
        }
        
        .info-item.full-width {
          grid-column: span 2;
        }
        
        .info-item label {
          font-size: 12px;
          color: #999;
          display: block;
          margin-bottom: 5px;
        }
        
        .info-item p {
          font-size: 14px;
          color: #1A1A1A;
          word-break: break-word;
        }
        
        .balance-locked { margin-top: 5px; }
        
        .pin-box {
          display: flex;
          gap: 10px;
          margin-top: 10px;
          flex-wrap: wrap;
        }
        
        .pin-input {
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 5px;
          font-size: 16px;
          width: 150px;
          min-height: 44px;
        }
        
        .decrypt-btn {
          padding: 12px 20px;
          background: #2E7D32;
          color: white;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          min-height: 44px;
        }
        
        .balance-value {
          font-size: 18px;
          font-weight: bold;
          color: #2E7D32;
        }
        
        .error-text {
          color: #dc2626;
          font-size: 12px;
          margin-top: 5px;
        }
        
        .notes-text {
          font-style: italic;
          color: #666;
        }
        
        .document-section {
          margin-top: 20px;
          padding: 20px;
          background: #F5F0E6;
          border-radius: 10px;
          margin-bottom: 20px;
        }
        
        .document-info { margin-top: 10px; }
        .document-info p { margin-bottom: 5px; font-size: 14px; }
        
        .proof-section {
          margin-top: 20px;
          padding: 20px;
          background: #F5F0E6;
          border-radius: 10px;
          margin-bottom: 20px;
        }
        
        .proof-box { margin-top: 10px; }
        .proof-label { font-size: 12px; color: #666; margin-bottom: 5px; }
        
        .proof-hash {
          display: block;
          background: #e5e7eb;
          padding: 12px;
          border-radius: 5px;
          font-size: 12px;
          word-break: break-all;
          font-family: monospace;
          margin-bottom: 10px;
        }
        
        .verified-status { color: #2E7D32; font-weight: bold; font-size: 14px; }
        .tampered-status { color: #dc2626; font-weight: bold; font-size: 14px; }
        .proof-date { font-size: 12px; color: #666; margin-top: 10px; }
        
        .beneficiary-section {
          margin-top: 20px;
          padding: 20px;
          background: #F5F0E6;
          border-radius: 10px;
        }
        
        .beneficiary-box { margin-top: 10px; }
        .beneficiary-box p { margin-bottom: 5px; font-size: 14px; }
        
        .loading-card, .error-card {
          background: white;
          border-radius: 10px;
          padding: 40px;
          text-align: center;
          max-width: 500px;
          margin: 100px auto;
        }
        
        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #F5F0E6;
          border-top: 4px solid #FFD700;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .back-button {
          display: inline-block;
          margin-top: 20px;
          padding: 10px 20px;
          background: #FFD700;
          color: #1A1A1A;
          text-decoration: none;
          border-radius: 5px;
        }
        
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        
        .modal-content {
          background: white;
          border-radius: 16px;
          padding: 30px;
          max-width: 450px;
          width: 90%;
          text-align: center;
        }
        
        .modal-icon { font-size: 48px; margin-bottom: 15px; }
        .modal-title { font-size: 24px; color: #dc2626; margin-bottom: 15px; }
        .modal-text { font-size: 16px; color: #333; margin-bottom: 15px; }
        .modal-warning { font-size: 14px; color: #666; background: #F5F0E6; padding: 12px; border-radius: 8px; margin-bottom: 25px; }
        .modal-buttons { display: flex; gap: 15px; justify-content: center; flex-wrap: wrap; }
        .modal-cancel, .modal-delete { padding: 12px 25px; border-radius: 8px; font-size: 14px; font-weight: bold; cursor: pointer; min-height: 44px; }
        .modal-cancel { background: transparent; border: 1px solid #ccc; }
        .modal-delete { background: #dc2626; border: none; color: white; }
        
        /* Asset Detail Sections */
        .asset-detail-section {
          grid-column: span 2;
          margin-top: 10px;
        }
        
        .section-title {
          font-size: 16px;
          font-weight: bold;
          color: #2E7D32;
          margin-bottom: 15px;
          padding-bottom: 5px;
          border-bottom: 2px solid #FFD700;
        }
        
        /* Secure Note Styles */
        .secure-note-content {
          background: #F5F0E6;
          border-radius: 8px;
          padding: 20px;
        }
        
        .secure-note-field {
          margin-bottom: 20px;
          padding: 12px;
          background: white;
          border-radius: 8px;
          border-left: 4px solid #FFD700;
        }
        
        .secure-note-field label {
          display: block;
          font-size: 12px;
          font-weight: bold;
          color: #FFD700;
          margin-bottom: 8px;
          text-transform: uppercase;
        }
        
        .secure-note-field p {
          font-size: 15px;
          font-weight: 500;
          color: #1A1A1A;
          margin: 0;
        }
        
        .secure-note-text {
          font-size: 14px;
          color: #1A1A1A;
          line-height: 1.6;
          white-space: pre-wrap;
          word-break: break-word;
          background: white;
          padding: 16px;
          border-radius: 8px;
          border: 1px solid #e0e0e0;
          margin-top: 5px;
        }
        
        /* Login Credential Styles */
        .login-credential-content {
          background: #F5F0E6;
          border-radius: 8px;
          padding: 20px;
        }
        
        .credential-field {
          margin-bottom: 15px;
          padding: 10px;
          background: white;
          border-radius: 8px;
          border-left: 4px solid #FFD700;
        }
        
        .credential-field label {
          display: block;
          font-size: 12px;
          font-weight: bold;
          color: #FFD700;
          margin-bottom: 5px;
          text-transform: uppercase;
        }
        
        .credential-field p {
          font-size: 14px;
          color: #1A1A1A;
          margin: 0;
          word-break: break-word;
        }
        
        .password-value {
          font-family: monospace;
          font-size: 14px;
          background: #f0f0f0;
          padding: 4px 8px;
          border-radius: 4px;
          display: inline-block;
        }
        
        .credential-field a {
          color: #2E7D32;
          text-decoration: none;
          word-break: break-all;
        }
        
        .credential-field a:hover {
          text-decoration: underline;
        }
        
        /* License/Code Styles */
        .license-content {
          background: #F5F0E6;
          border-radius: 8px;
          padding: 20px;
        }
        
        .license-field {
          margin-bottom: 15px;
          padding: 10px;
          background: white;
          border-radius: 8px;
          border-left: 4px solid #FFD700;
        }
        
        .license-field label {
          display: block;
          font-size: 12px;
          font-weight: bold;
          color: #FFD700;
          margin-bottom: 5px;
          text-transform: uppercase;
        }
        
        .license-field p {
          font-size: 14px;
          color: #1A1A1A;
          margin: 0;
          word-break: break-word;
        }
        
        .license-value {
          font-family: monospace;
          font-size: 13px;
          background: #f0f0f0;
          padding: 4px 8px;
          border-radius: 4px;
          display: inline-block;
        }
        
        /* Custom Asset Styles */
        .custom-asset-content {
          background: #F5F0E6;
          border-radius: 8px;
          padding: 20px;
        }
        
        .custom-field {
          margin-bottom: 15px;
          padding: 12px;
          background: white;
          border-radius: 8px;
          border-left: 4px solid #FFD700;
        }
        
        .custom-field label {
          display: block;
          font-size: 12px;
          font-weight: bold;
          color: #FFD700;
          margin-bottom: 6px;
          text-transform: uppercase;
        }
        
        .custom-field p {
          font-size: 14px;
          color: #1A1A1A;
          margin: 0;
          word-break: break-word;
        }
        
        .no-fields {
          font-size: 14px;
          color: #666;
          text-align: center;
          padding: 20px;
        }
        
        /* Responsive */
        @media (max-width: 768px) {
          .asset-details-container { padding: 15px; }
          .asset-header { flex-direction: column; text-align: center; }
          .asset-title { font-size: 20px; }
          .header-buttons { justify-content: center; }
          .asset-card { padding: 20px; }
          .info-grid { grid-template-columns: 1fr; gap: 15px; }
          .info-item.full-width { grid-column: span 1; }
          .asset-detail-section { grid-column: span 1; }
          .pin-box { flex-direction: column; align-items: stretch; }
          .pin-input { width: 100%; text-align: center; }
          .proof-hash { font-size: 10px; }
          .modal-content { padding: 20px; width: 95%; }
          .modal-title { font-size: 20px; }
          .modal-buttons { flex-direction: column; }
          .modal-cancel, .modal-delete { width: 100%; }
        }
        
        @media (max-width: 480px) {
          .asset-title { font-size: 18px; }
          .btn-pdf, .btn-doc, .btn-delete { padding: 8px 16px; font-size: 12px; }
          .type-badge { gap: 8px; padding: 6px 12px; justify-content: center; }
          .type-text { font-size: 12px; }
          .cert-badge { font-size: 10px; padding: 3px 6px; }
          .info-item p { font-size: 13px; }
        }
      `}} />
      
    </div>
  );
};

export default AssetDetails;